import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { AlertTriangle, ChevronDown, Inbox, ListFilter, Search, SlidersHorizontal } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import { emit } from '@/lib/analytics';
import { useWorkspace } from '@/contexts/workspace-context';
import { useDeliveriesStream } from '@/hooks/use-deliveries-stream';
import { useSubscriptionQuery } from '@/components/billing/queries';
import { useDeliveriesList } from './queries';
import { useDeliveryDetails } from './use-delivery-details';
import { DeliveryRow } from './delivery-row';
import { PlanFloorBanner } from './plan-floor-banner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import type { DeliveryLog, DeliveryLogRow, DeliveryStatus } from '@/types/delivery';
import {
  deliverySearchText,
  isMessageDelivery,
  relatedKeyFor,
  statusLabelFor,
  type ViewFilter,
} from './delivery-log-utils';
import { LogStream } from './delivery-row';

function buildDemoItems(): DeliveryLog[] {
  const now = Date.now();
  const ago = (ms: number) => new Date(now - ms).toISOString();
  const SEC = 1000;
  const MIN = 60 * SEC;
  const HR = 60 * MIN;
  const DAY = 24 * HR;
  const stub = (
    receivedAt: string,
    status: DeliveryStatus,
    statusText: string,
    sender: string | null,
    suffix: string,
  ): DeliveryLog => ({
    receivedAt,
    sender,
    messageId: `wamid.demo-${suffix}`,
    meta: {
      object: 'whatsapp_business_account',
      entry: [
        {
          id: '1276334778010256',
          changes: [
            {
              field: 'messages',
              value: {
                messaging_product: 'whatsapp',
                messages: [
                  {
                    from: sender ?? '15555550100',
                    id: `wamid.demo-${suffix}`,
                    timestamp: String(Math.floor(new Date(receivedAt).getTime() / 1000)),
                    text: { body: 'Hello from demo data' },
                    type: 'text',
                  },
                ],
              },
            },
          ],
        },
      ],
    },
    hookmyapp: {
      status,
      statusText,
      destination:
        status === 'skipped' || status === 'not_delivered'
          ? null
          : { type: 'webhook', url: 'https://your-app.example.com/webhook' },
      appResponse:
        status === 'delivered'
          ? { status: 200, durationMs: 124, body: { ok: true } }
          : status === 'rejected'
            ? { status: 500, durationMs: 124, body: { error: 'internal' } }
            : { status: null, durationMs: null, body: null },
    },
  });
  const D = 'Delivered to your app';
  const R = "Your app couldn't process it";
  const N = "Your app didn't reply in time";
  const X = 'No sandbox destination';
  return [
    stub(ago(2 * SEC), 'delivered', D, '972545434384', '2s'),
    stub(ago(8 * SEC), 'delivered', D, '972501234567', '8s'),
    stub(ago(25 * SEC), 'rejected', R, '972545434384', '25s'),
    stub(ago(1 * MIN + 10 * SEC), 'delivered', D, '14155551212', '1m'),
    stub(ago(3 * MIN), 'delivered', D, '972545434384', '3m'),
    stub(ago(7 * MIN), 'no_response', N, '12025550100', '7m'),
    stub(ago(15 * MIN), 'delivered', D, '972501234567', '15m'),
    stub(ago(28 * MIN), 'delivered', D, '447700900000', '28m'),
    stub(ago(45 * MIN), 'not_delivered', X, '972545434384', '45m'),
    stub(ago(1 * HR + 5 * MIN), 'delivered', D, '972501234567', '1h'),
    stub(ago(2 * HR), 'rejected', R, '14155551212', '2h'),
    stub(ago(4 * HR + 30 * MIN), 'delivered', D, '972545434384', '4h'),
    stub(ago(8 * HR), 'delivered', D, '972501234567', '8h'),
    stub(ago(14 * HR), 'no_response', N, '12025550100', '14h'),
    stub(ago(22 * HR), 'delivered', D, '972545434384', '22h'),
    stub(ago(1 * DAY + 6 * HR), 'delivered', D, '972501234567', '1d6h'),
    stub(ago(2 * DAY + 3 * HR), 'not_delivered', X, '14155551212', '2d3h'),
    stub(ago(3 * DAY), 'delivered', D, '972545434384', '3d'),
    stub(ago(5 * DAY), 'delivered', D, '972501234567', '5d'),
    stub(ago(30 * DAY), 'delivered', D, '12025550100', '30d'),
  ];
}

interface DeliveriesPanelProps {
  scope: string;
  channelType?: 'whatsapp' | 'instagram' | 'facebook';
}

export function DeliveriesPanel({ scope, channelType }: DeliveriesPanelProps) {
  const { activeWorkspace, sessionScoped } = useWorkspace();
  const listQuery = useDeliveriesList(activeWorkspace?.id, scope, sessionScoped);
  const subscriptionQuery = useSubscriptionQuery(activeWorkspace?.organizationPublicId ?? null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const { detailById, ensureDetail } = useDeliveryDetails();
  const [verbose, setVerbose] = useState(false);
  const [search, setSearch] = useState('');
  const [viewFilter, setViewFilter] = useState<ViewFilter>('all');
  const stream = useDeliveriesStream({ scope });

  const scopeKind: 'channel' | 'sandbox-session' = scope.startsWith('channel:')
    ? 'channel'
    : 'sandbox-session';
  const mountedAtRef = useRef<number>(Date.now());
  const viewedRef = useRef(false);
  const sessionEndedFloorRef = useRef<number>(168);

  const demoItems = useMemo(
    () =>
      import.meta.env.DEV &&
      typeof window !== 'undefined' &&
      window.location.search.includes('demo=1')
        ? buildDemoItems()
        : [],
    [],
  );

  const items = useMemo(
    () => [...demoItems, ...(listQuery.data?.logs ?? [])],
    [demoItems, listQuery.data?.logs],
  );
  const subscriptionResolved = activeWorkspace?.organizationPublicId
    ? !subscriptionQuery.isPending
    : true;
  const floorHours = subscriptionQuery.data?.plan.slug === 'free' ? 24 : 168;
  const loading = listQuery.isPending;

  useEffect(() => {
    if (loading || !subscriptionResolved || viewedRef.current) return;
    viewedRef.current = true;
    sessionEndedFloorRef.current = floorHours;
    emit(
      'deliveries_page_viewed',
      {
        scope_kind: scopeKind,
        floor_hours: floorHours,
        forward_attempts_visible: items.filter(
          (i) =>
            i.hookmyapp.destination !== null ||
            i.hookmyapp.appResponse.status !== null ||
            i.hookmyapp.appResponse.durationMs !== null,
        ).length,
        is_paid: floorHours >= 168,
      },
      // Guard on presence — an empty-string group key would mint a junk
      // PostHog group (AIT-69 item 5).
      activeWorkspace?.id ? { groups: { workspace: activeWorkspace.id } } : {},
    );
  }, [
    loading,
    subscriptionResolved,
    floorHours,
    items,
    scopeKind,
    activeWorkspace?.id,
  ]);

  useEffect(() => {
    sessionEndedFloorRef.current = floorHours;
  }, [floorHours]);

  useEffect(() => {
    const mountedAt = mountedAtRef.current;
    return () => {
      const finalFloor = sessionEndedFloorRef.current;
      emit(
        'deliveries_page_session_ended',
        {
          scope_kind: scopeKind,
          is_paid: finalFloor >= 168,
          duration_ms: Date.now() - mountedAt,
        },
        activeWorkspace?.id ? { groups: { workspace: activeWorkspace.id } } : {},
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- unmount-only
  }, []);

  useEffect(() => {
    for (const live of stream.items) {
      ensureDetail(live.publicId);
    }
  }, [stream.items, ensureDetail]);

  const merged = useMemo(() => {
    const logs = [...items];
    const seen = new Set(logs.map(deliveryFingerprint));
    for (const live of stream.items) {
      const detail = detailById[live.publicId];
      if (!detail) continue;
      const fingerprint = deliveryFingerprint(detail);
      if (seen.has(fingerprint)) continue;
      seen.add(fingerprint);
      logs.push(detail);
    }
    return withRowKeys(logs).sort(
      (a, b) =>
        new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime(),
    );
  }, [items, stream.items, detailById]);

  const relatedByKey = useMemo(() => {
    const out: Record<string, RelatedDeliveryView[]> = {};
    for (const row of merged) out[row.rowKey] = relatedLogsFor(row, merged);
    return out;
  }, [merged]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = merged;
    if (q) {
      rows = rows.filter((d) => deliverySearchText(d).includes(q));
    }
    if (viewFilter !== 'all') {
      // AIT-184 + AIT-549: see isMessageDelivery.
      rows = rows.filter(isMessageDelivery);
    }
    return rows;
  }, [merged, search, viewFilter]);

  const toggleExpand = useCallback(
    (key: string) => {
      setExpandedKey((current) => (current === key ? null : key));
    },
    [],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <PlanFloorBanner
        floorHours={floorHours}
        workspacePublicId={activeWorkspace?.id ?? ''}
        scopeKind={scopeKind}
      />
      {stream.takenOver ? (
        <Alert className="w-fit max-w-full items-start border-amber-500/30 bg-amber-500/5 sm:items-center [&>svg]:translate-y-0.5 [&>svg]:row-span-1 sm:[&>svg]:translate-y-0">
          <AlertTriangle className="text-amber-600 dark:text-amber-400" />
          <AlertDescription className="flex flex-col items-start gap-2 text-amber-700 sm:flex-row sm:items-center sm:gap-4 dark:text-amber-300">
            <span>
              Live log streaming is active in another tab. Switch it here to
              watch new logs as they come in.
            </span>
            <Button size="sm" onClick={stream.reclaim}>
              Use Here
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}
      <Card className="min-h-0 flex-1 overflow-hidden">
        <CardContent className="flex h-full min-h-0 flex-col gap-2 px-0 pb-0">
          <div className="flex items-center gap-2 px-4">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <h3 className="hidden text-sm font-medium text-muted-foreground sm:block">
                Logs
              </h3>
              {!loading && merged.length > 0 ? (
                <div className="relative max-w-xs flex-1">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search logs"
                    aria-label="Search deliveries"
                    className="h-7 pl-7 text-xs"
                  />
                </div>
              ) : null}
              {!loading && merged.length > 0 ? (
                <span className="hidden font-mono text-xs text-muted-foreground sm:inline">
                  {filtered.length === merged.length
                    ? `${merged.length} ${merged.length === 1 ? 'event' : 'events'}`
                    : `${filtered.length}/${merged.length}`}
                </span>
              ) : null}
            </div>

            {/* Desktop controls: inline tabs + verbose switch */}
            <div className="hidden items-center gap-3 sm:flex">
              {!loading && merged.length > 0 ? (
                <Tabs
                  value={viewFilter}
                  onValueChange={(v) => setViewFilter(v as ViewFilter)}
                >
                  <TabsList className="h-7">
                    <TabsTrigger value="all" className="px-3 text-xs">
                      All
                    </TabsTrigger>
                    <TabsTrigger value="messages" className="px-3 text-xs">
                      Messages
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              ) : null}
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                Verbose
                <Switch
                  checked={verbose}
                  onCheckedChange={(v) => setVerbose(Boolean(v))}
                  aria-label="Verbose log view"
                />
              </label>
            </div>

            {/* Mobile controls: filter + verbose collapse into dropdown menus */}
            <div className="flex items-center gap-1 sm:hidden">
              {!loading && merged.length > 0 ? (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <Button variant="outline" size="sm" className="gap-1">
                        <ListFilter className="size-3.5" />
                        {viewFilter === 'all' ? 'All' : 'Messages'}
                        <ChevronDown className="size-3.5" />
                      </Button>
                    }
                  />
                  <DropdownMenuContent align="end">
                    <DropdownMenuRadioGroup
                      value={viewFilter}
                      onValueChange={(v) => setViewFilter(v as ViewFilter)}
                    >
                      <DropdownMenuRadioItem value="all">All</DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="messages">
                        Messages
                      </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      variant="outline"
                      size="sm"
                      aria-label="Log view options"
                      className="gap-1"
                    >
                      <SlidersHorizontal className="size-3.5" />
                      <ChevronDown className="size-3.5" />
                    </Button>
                  }
                />
                <DropdownMenuContent align="end">
                  <DropdownMenuCheckboxItem
                    checked={verbose}
                    onCheckedChange={(v) => setVerbose(Boolean(v))}
                  >
                    Verbose
                  </DropdownMenuCheckboxItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto border-t bg-background/40">
            {loading ? (
              <div className="px-4 py-3 text-sm text-muted-foreground">Loading...</div>
            ) : merged.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-12 text-center">
                <div className="flex size-12 items-center justify-center rounded-full bg-muted">
                  <Inbox className="size-6 text-muted-foreground" />
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-medium">No deliveries yet</h4>
                  <p className="max-w-xs text-sm text-muted-foreground">
                    Send a message to this {scopeKind === 'sandbox-session' ? 'sandbox' : 'channel'} to see webhook deliveries appear here in real time.
                  </p>
                </div>
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                {search.trim()
                  ? `No deliveries match "${search}".`
                  : viewFilter === 'messages'
                    ? 'No deliveries with messages.'
                    : 'No deliveries match.'}
              </div>
            ) : verbose ? (
              <LogStream
                items={filtered}
                relatedByKey={relatedByKey}
                viewFilter={viewFilter}
                channelType={channelType}
              />
            ) : (
              <div className="divide-y">
                {filtered.map((d) => (
                  <DeliveryRow
                    key={d.rowKey}
                    delivery={d}
                    relatedLogs={relatedByKey[d.rowKey] ?? []}
                    expanded={expandedKey === d.rowKey}
                    onToggle={() => toggleExpand(d.rowKey)}
                    viewFilter={viewFilter}
                    channelType={channelType}
                  />
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function deliveryFingerprint(log: DeliveryLog): string {
  // AIT-549: the wd_ id is the identity when present. A subscription attempt
  // shares receivedAt/messageId/sender/status with its primary row.
  if (log.publicId) return log.publicId;
  return [
    log.receivedAt,
    log.messageId ?? '',
    log.sender ?? '',
    log.hookmyapp.status,
  ].join('|');
}

function baseRowKeyFor(log: DeliveryLog): string {
  // AIT-549: the wd_ id is the identity when present. A subscription attempt
  // shares receivedAt/messageId/sender/status with its primary row.
  if (log.publicId) return log.publicId;
  return [
    log.receivedAt,
    log.messageId ?? 'no-message-id',
    log.sender ?? 'unknown-sender',
    log.hookmyapp.status,
  ].join(':');
}

function withRowKeys(logs: DeliveryLog[]): DeliveryLogRow[] {
  const seen = new Map<string, number>();
  return logs.map((log) => {
    const base = baseRowKeyFor(log);
    const occurrence = seen.get(base) ?? 0;
    seen.set(base, occurrence + 1);
    return {
      ...log,
      rowKey: occurrence === 0 ? base : `${base}#${occurrence}`,
    };
  });
}

export interface RelatedDeliveryView {
  rowKey: string;
  receivedAt: string;
  status: DeliveryStatus;
  statusLabel: string;
}

function relatedLogsFor(
  row: DeliveryLogRow,
  rows: DeliveryLogRow[],
): RelatedDeliveryView[] {
  const key = relatedKeyFor(row);
  if (!key) return [];
  return rows
    .filter((candidate) => candidate.rowKey !== row.rowKey && relatedKeyFor(candidate) === key)
    .map((candidate) => ({
      rowKey: candidate.rowKey,
      receivedAt: candidate.receivedAt,
      status: candidate.hookmyapp.status,
      statusLabel: statusLabelFor(candidate),
    }));
}
